import {
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { Info } from 'lucide-react';

import {
  type VisibilityCountry,
  type VisibilityPositionBreakdown,
  formatVisibilitySizeMultiplier,
} from '@modules/_shared/visibility-scoring';
import {
  type HealthTone,
  getHealthMainColor,
} from '@modules/organisations/OrganisationEngagementCard/healthTone';
import type { VisibilityScoreResult } from '@modules/organisations/visibilityScorePresenter';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const POPOVER_MIN_WIDTH = 360;
const POPOVER_MAX_WIDTH = 480;
const POPOVER_MAX_HEIGHT = 540;

const formatNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale).format(Math.round(value));

const getSignalTone = (
  color: VisibilityScoreResult['signals'][number]['color'],
): HealthTone => {
  switch (color) {
    case 'success':
      return 'healthy';
    case 'warning':
      return 'fair';
    case 'error':
      return 'atRisk';
    case 'disabled':
    default:
      return 'neutral';
  }
};

const PopoverHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {subtitle}
    </Typography>
  </Box>
);

const PopoverFooter = ({ legend }: { legend: string }) => (
  <Box
    sx={{
      p: 1.25,
      borderTop: '1px solid',
      borderColor: 'divider',
      bgcolor: 'action.hover',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {legend}
    </Typography>
  </Box>
);

const InfoCallout = ({
  title,
  body,
}: {
  title: string;
  body: string | React.ReactNode;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'flex-start',
        p: 1.25,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Info
        size={14}
        color={customPalette.gray600}
        style={{ flexShrink: 0, marginTop: 2 }}
        aria-hidden
      />
      <Box>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontWeight: 600, display: 'block' }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
};

const ProgressBlock = ({
  label,
  current,
  target,
  unitLabel,
  tone,
  pointsLabel,
}: {
  label: string;
  current: number;
  target: number;
  unitLabel: string;
  tone: HealthTone;
  pointsLabel: string;
}) => {
  const theme = useTheme();
  const toneColor = getHealthMainColor(theme, tone);
  const percent =
    target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;

  return (
    <Stack spacing={0.75}>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {pointsLabel}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 6,
          borderRadius: 999,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            bgcolor: toneColor,
          },
        }}
      />
      <Typography variant="caption" color="text.disabled">
        {Math.round(current).toLocaleString()} /{' '}
        {Math.round(target).toLocaleString()} {unitLabel}
      </Typography>
    </Stack>
  );
};

const PositionBreakdownTable = ({
  breakdown,
  locale,
  t,
}: {
  breakdown: VisibilityPositionBreakdown[];
  locale: string;
  t: ReturnType<typeof useLocale>['t'];
}) => {
  if (breakdown.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled">
        {t('No impressions in positions 1–7 yet.')}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
      >
        {t('Effective impressions by rank')}
      </Typography>
      <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t('Position')}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {t('Impressions')}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {t('Weight')}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {t('Effective')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {breakdown.map(row => (
            <TableRow key={row.position}>
              <TableCell>P{row.position}</TableCell>
              <TableCell align="right">
                {formatNumber(row.count, locale)}
              </TableCell>
              <TableCell align="right">{row.weight.toFixed(2)}</TableCell>
              <TableCell align="right">
                {formatNumber(row.weighted, locale)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const COUNTRY_LABEL: Record<VisibilityCountry, string> = {
  CH: 'CH',
  ES: 'ES',
  FR: 'FR',
  IT: 'IT',
};

export const VisibilitySignalPopover = ({
  signalId,
  result,
}: {
  signalId: 'profileViews' | 'valuationWeighted';
  result: VisibilityScoreResult;
}) => {
  const { t, locale } = useLocale();

  const signal = result.signals.find(s => s.id === signalId);
  if (signal == null) {
    return null;
  }

  const tone = getSignalTone(signal.color);
  const multiplierLabel = formatVisibilitySizeMultiplier(result.sizeMultiplier);
  const tierExplanation =
    result.sizeMultiplier === 1
      ? t(
          'Your size multiplier is 1.0x — based on {{count}} published transaction in the last 24 months.',
          { count: result.transactionsCount24mo },
        )
      : t(
          'Your size multiplier is {{multiplier}} — based on {{count}} published transactions in the last 24 months.',
          {
            multiplier: multiplierLabel,
            count: result.transactionsCount24mo,
          },
        );

  return (
    <Box
      sx={{
        minWidth: POPOVER_MIN_WIDTH,
        maxWidth: POPOVER_MAX_WIDTH,
        maxHeight: POPOVER_MAX_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PopoverHeader
        title={signal.label}
        subtitle={
          signalId === 'profileViews'
            ? t(
                'Unique visitors to your agent and agency profile pages (last 90 days).',
              )
            : t(
                'Weighted impressions in valuation search results, positions 1–7 (last 90 days).',
              )
        }
      />

      <Stack
        spacing={2}
        sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        {signalId === 'profileViews' && (
          <>
            <ProgressBlock
              label={t('Toward full bar (50 pts)')}
              current={result.profileViewsTotal}
              target={result.profileViewsTarget}
              unitLabel={t('views')}
              tone={tone}
              pointsLabel={t('{{points}} pts', { points: signal.points })}
            />
            <InfoCallout
              title={t('How it scores')}
              body={t(
                '1 unique profile visitor = 1 point. Target {{target}} views to fill the bar.',
                { target: Math.round(result.profileViewsTarget) },
              )}
            />
          </>
        )}

        {signalId === 'valuationWeighted' && (
          <>
            <ProgressBlock
              label={t('Toward full bar (50 pts)')}
              current={result.effectiveImpressions}
              target={result.targetImpressions}
              unitLabel={t('effective impressions')}
              tone={tone}
              pointsLabel={t('{{points}} pts', { points: signal.points })}
            />
            <PositionBreakdownTable
              breakdown={result.positionBreakdown}
              locale={locale}
              t={t}
            />
            <InfoCallout
              title={t('How it scores')}
              body={t(
                'Impressions are weighted by position (P1 = 1.00, P7 = 0.31). Country target ({{country}}) × {{multiplier}} = {{target}} effective impressions to fill the bar.',
                {
                  country:
                    result.country != null
                      ? COUNTRY_LABEL[result.country]
                      : 'ES',
                  multiplier: multiplierLabel,
                  target: Math.round(result.targetImpressions),
                },
              )}
            />
          </>
        )}

        <InfoCallout title={t('Size target')} body={tierExplanation} />
      </Stack>

      <PopoverFooter
        legend={t(
          'Score = 75% valuation views + 25% profile views. The gauge can exceed 100% (shown as +100%) when a signal overflows its target.',
        )}
      />
    </Box>
  );
};
